import {defineStore} from 'pinia';
import {ref, computed} from 'vue';

export type Layer = {
  id: number;
  name: string;
  isVisible: boolean;
};

type LayerInfo = Layer & {
  defaultIsVisible: boolean;
};

export const BACKGROUND_LAYER: Layer = {id: 76, name: 'Background', isVisible: true};
export const UNKNOWN_LAYER: Layer = {id: 0, name: 'Unknown', isVisible: true};
export const VIA_HOLES: Layer = {id: 82, name: 'Via Holes', isVisible: true};
export const PAD_HOLES: Layer = {id: 81, name: 'Pad Holes', isVisible: true};

export const useLayersStore = defineStore('layers', () => {
  const layers = ref<LayerInfo[]>([]);

  const hasLayers = computed(() => {
    return layers.value.length > 0;
  });

  const visibleLayers = computed(() => {
    return layers.value.filter((layer) => layer.isVisible);
  });

  // this is used so commonly that it's worth putting it here so it's only recomputed once
  const visibleLayerIds = computed(() => {
    return visibleLayers.value.map((layer) => layer.id);
  });

  function toggleAllLayers(visibility: boolean) {
    for (const layer of layers.value) {
      layer.isVisible = visibility;
    }
  }

  function showPreset(presetLayerIds: number[]) {
    for (const layer of layers.value) {
      layer.isVisible = presetLayerIds.includes(layer.id);
    }
  }

  function toggleLayer(layerId: number, set?: boolean) {
    const layer = layers.value.find((layer) => layer.id === layerId);
    if (layer) {
      layer.isVisible = set === undefined ? !layer.isVisible : set;
      return layer.isVisible;
    }
    return null;
  }

  function isLayerVisible(layerId: number) {
    const layer = layers.value.find((layer) => layer.id === layerId);
    if (layer) {
      return layer.isVisible;
    }
    return false;
  }

  function resetToDefaults() {
    for (const layer of layers.value) {
      layer.isVisible = layer.defaultIsVisible;
    }
  }

  function $reset() {
    layers.value = [];
  }

  return {
    layers,
    hasLayers,
    visibleLayers,
    visibleLayerIds,
    toggleAllLayers,
    showPreset,
    toggleLayer,
    isLayerVisible,
    resetToDefaults,
    $reset,
  };
});
